import { cloneElement, isValidElement, useEffect, useId, useState, type ReactElement } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AlertCircle, Loader2, PlusCircle, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AddressAutocompleteFields } from "@/components/address/AddressAutocompleteFields";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompanySelect } from "@/components/common/entity-select";
import { OrganizationSelect } from "@/components/common/entity-select/OrganizationSelect";
import { dataApi } from "@/lib/api/admin-api";
import { useAuth } from "@/hooks/useAuth";
import { canAccessAction } from "@/lib/access-control/ui-access";
import {
  APP_TIMEZONE_OPTIONS,
  DEFAULT_APP_TIMEZONE,
  normalizeAppTimezone,
} from "@/lib/config/timezones";
import {
  ACTIVE_ADDRESS_COUNTRY,
  isPostalCodeValid,
} from "@/lib/config/address";
import { formatUsPhone, isValidUsPhone, normalizeUsPhone } from "@/lib/phone";

const locationFormSchema = z.object({
  companyId: z.string().min(1, t`Please select a concept`),
  regionId: z.string().optional(),
  name: z.string().min(1, t`Location name is required`),
  code: z.string().optional(),
  timezone: z.string().min(1, t`Timezone is required`),
  addressLine1: z.string().min(1, t`Address is required`),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().min(1, t`ZIP code is required`),
  country: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  email: z
    .union([z.literal(""), z.string().email(t`Enter a valid email address`)])
    .optional(),
  phone: z.string().optional(),
  isActive: z.boolean().optional(),
}).superRefine((value, ctx) => {
  if (!isPostalCodeValid(value.country ?? ACTIVE_ADDRESS_COUNTRY, value.postalCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t`Enter a valid ZIP code`,
      path: ["postalCode"],
    });
  }

  // Optional, so an empty box is fine — a half-typed number is not.
  if (value.phone && !isValidUsPhone(value.phone)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t`Enter a 10-digit phone number`,
      path: ["phone"],
    });
  }
});

type LocationFormValues = z.infer<typeof locationFormSchema>;

const inputClass = "";
const selectTriggerClass = "";

function FormSection({
  title,
  description,
  children,
  noBorder,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <div className={`flex ${noBorder ? "" : "border-b border-(--color-border)"}`}>
      <div className="w-[300px] shrink-0 bg-(--color-surface-muted) border-r border-(--color-border) p-5 flex flex-col gap-2">
        <h3 className="text-sm font-medium text-(--color-text-primary)">{title}</h3>
        {description && (
          <p className="text-sm text-(--color-text-secondary) leading-snug">{description}</p>
        )}
      </div>
      <div className="flex-1 p-5 flex flex-col gap-4">{children}</div>
    </div>
  );
}

function FormField({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      <Label htmlFor={id} className="text-sm font-medium text-(--color-text-primary)">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-(--color-danger-text)">{error}</p>}
    </div>
  );
}

interface LocationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCompanyId?: number;
  defaultRegionId?: number;
  onCreated?: () => void;
}

function LocationFormDialog({
  open,
  onOpenChange,
  defaultCompanyId,
  defaultRegionId,
  onCreated,
}: LocationFormDialogProps) {
  const { t } = useLingui();
  const { user, hasPermission, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const idPrefix = useId();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const accessInput = { user, hasPermission, hasRole };
  const canCreateLocation = canAccessAction(accessInput, { permissionsAny: ["location:create"] });

  const userOrgId = user?.organization?.id;
  const isInternalOrgSelectorRequired = hasRole("SUPER_ADMIN") || hasRole("SUPPORT");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(
    isInternalOrgSelectorRequired ? null : (userOrgId ?? null),
  );
  const [selectedOrganizationName, setSelectedOrganizationName] = useState("");
  const effectiveOrganizationId = isInternalOrgSelectorRequired
    ? selectedOrganizationId
    : (userOrgId ?? null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<LocationFormValues>({
    resolver: zodResolver(locationFormSchema),
    mode: "onBlur",
    defaultValues: {
      companyId: "",
      regionId: "",
      name: "",
      code: "",
      timezone: DEFAULT_APP_TIMEZONE,
      addressLine1: "",
      city: "",
      state: "",
      postalCode: "",
      country: ACTIVE_ADDRESS_COUNTRY,
      latitude: undefined,
      longitude: undefined,
      email: "",
      phone: "",
      isActive: true,
    },
  });

  const { data: companyRegions = [] } = useQuery({
    queryKey: ["regions", "for-location-create", effectiveOrganizationId],
    queryFn: () => effectiveOrganizationId ? dataApi.getOrganizationRegions(effectiveOrganizationId) : Promise.resolve([]),
    enabled: open && !!effectiveOrganizationId,
  });

  useEffect(() => {
    if (open) {
      reset({
        companyId: defaultCompanyId ? String(defaultCompanyId) : "",
        regionId: defaultRegionId ? String(defaultRegionId) : "",
        name: "",
        code: "",
        timezone: DEFAULT_APP_TIMEZONE,
        addressLine1: "",
        city: "",
        state: "",
        postalCode: "",
        country: ACTIVE_ADDRESS_COUNTRY,
        latitude: undefined,
        longitude: undefined,
        email: "",
        phone: "",
        isActive: true,
      });
    }
  }, [defaultCompanyId, defaultRegionId, open, reset]);

  const createMutation = useMutation({
    mutationFn: (data: LocationFormValues) =>
      dataApi.createLocation({
        companyId: Number(data.companyId),
        regionId: data.regionId ? Number(data.regionId) : undefined,
        name: data.name.trim(),
        code: data.code?.trim() || undefined,
        timezone: normalizeAppTimezone(data.timezone),
        addressLine1: data.addressLine1.trim(),
        city: data.city?.trim() || undefined,
        state: data.state?.trim() || undefined,
        postalCode: data.postalCode.trim(),
        country: ACTIVE_ADDRESS_COUNTRY,
        latitude: data.latitude,
        longitude: data.longitude,
        email: data.email?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        isActive: data.isActive ?? true,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      onCreated?.();
      onOpenChange(false);
      toast.success(t`Location created successfully`);
      // Take the user to what they just made instead of leaving them on the
      // concept or region page they opened the dialog from. A response without
      // an id would only produce a broken route, so stay put in that case.
      if (created?.id !== undefined && created?.id !== null) {
        void navigate({
          to: "/locations/$locationId",
          params: { locationId: String(created.id) },
        });
      }
    },
    onError: (error: Error) => {
      setSubmitError(error.message || t`Failed to create location`);
    },
  });

  const onSubmit = (data: LocationFormValues) => {
    setSubmitError(null);

    if (!canCreateLocation) {
      setSubmitError(t`Access Restricted`);
      return;
    }

    if (isInternalOrgSelectorRequired && !selectedOrganizationId) {
      setSubmitError(t`Please select an organization`);
      return;
    }

    if (!data.companyId) {
      setError("companyId", {
        type: "required",
        message: t`Please select a concept`,
      });
      return;
    }

    clearErrors("companyId");
    createMutation.mutate(data);
  };

  const isPending = createMutation.isPending;
  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setSubmitError(null);
      setSelectedOrganizationId(isInternalOrgSelectorRequired ? null : (userOrgId ?? null));
      setSelectedOrganizationName("");
      setValue("companyId", "");
      setValue("regionId", "");
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[900px] p-0 overflow-hidden gap-0 border-none rounded-(--radius-lg)"
      >
        <div className="flex flex-col bg-(--color-surface-content) max-h-[90vh]">
          <div className="flex items-start justify-between p-5 border-b border-(--color-border)">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-none">
                {t`Create Location`}
              </DialogTitle>
              <DialogDescription className="text-base text-(--color-text-secondary) font-normal leading-none">
                {t`Select a concept, then add location details.`}
              </DialogDescription>
            </div>
            <DialogClose
              aria-label={t`Close dialog`}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <X className="h-6 w-6" />
            </DialogClose>
          </div>

          <form
            id={`${idPrefix}-form`}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col flex-1 overflow-y-auto"
          >
            <FormSection
              title={t`General details`}
              description={t`Basic information for this location.`}
            >
              {isInternalOrgSelectorRequired && (
                <div className="flex gap-3">
                  <FormField
                    label={t`Organization`}
                    error={!selectedOrganizationId ? t`Please select an organization` : undefined}
                  >
                    <OrganizationSelect
                      value={selectedOrganizationId}
                      adminScope={true}
                      displayValue={selectedOrganizationName || undefined}
                      onChange={(orgId, organization) => {
                        setSelectedOrganizationId(orgId);
                        setSelectedOrganizationName(organization.name);
                        setSubmitError(null);
                        setValue("companyId", "");
                        setValue("regionId", "");
                        clearErrors("companyId");
                      }}
                      onClear={() => {
                        setSelectedOrganizationId(null);
                        setSelectedOrganizationName("");
                        setValue("companyId", "");
                        setValue("regionId", "");
                      }}
                      triggerClassName={selectTriggerClass}
                    />
                  </FormField>
                </div>
              )}

              <div className="flex gap-3">
                <FormField
                  label={t`Concept`}
                  error={errors.companyId?.message}
                >
                  <Controller
                    name="companyId"
                    control={control}
                    render={({ field }) => (
                      <CompanySelect
                        value={field.value ? Number(field.value) : null}
                        organizationId={effectiveOrganizationId ?? undefined}
                        disabled={isInternalOrgSelectorRequired && !selectedOrganizationId}
                        onChange={(value) => {
                          field.onChange(String(value));
                          clearErrors("companyId");
                        }}
                        placeholder={t`Select a concept`}
                      />
                    )}
                  />
                </FormField>

                <FormField label={t`Region (optional)`}>
                  <Controller
                    name="regionId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value === "none" ? "" : value);
                        }}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue placeholder={t`Select`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t`Not set`}</SelectItem>
                          {companyRegions.map((region) => (
                            <SelectItem key={region.id} value={String(region.id)}>
                              {region.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              </div>

              <div className="flex gap-3">
                <FormField label={t`Location Name`} error={errors.name?.message}>
                  <Input
                    placeholder={t`Enter location name...`}
                    {...register("name")}
                    className={`${inputClass} ${errors.name ? "border-(--color-danger)" : ""}`}
                    autoFocus
                  />
                </FormField>
                <FormField label={t`Code (optional)`} error={errors.code?.message}>
                  <Input
                    placeholder={t`e.g., DT-001`}
                    {...register("code")}
                    className={`${inputClass} ${errors.code ? "border-(--color-danger)" : ""}`}
                  />
                  <p className="text-xs text-(--color-text-secondary)">
                    {t`Leave blank to generate one from the concept name.`}
                  </p>
                </FormField>
              </div>

              <div className="flex gap-3">
                <FormField label={t`Timezone`} error={errors.timezone?.message}>
                  <Controller
                    name="timezone"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={normalizeAppTimezone(field.value)}
                        onValueChange={(value) => {
                          field.onChange(value);
                          clearErrors("timezone");
                        }}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue placeholder={t`Select timezone`} />
                        </SelectTrigger>
                        <SelectContent>
                          {APP_TIMEZONE_OPTIONS.map((timezone) => (
                            <SelectItem key={timezone.value} value={timezone.value}>
                              {timezone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
                <FormField label={t`Status`}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ? "active" : "inactive"}
                        onValueChange={(value) => field.onChange(value === "active")}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t`Active`}</SelectItem>
                          <SelectItem value="inactive">{t`Inactive`}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              </div>
            </FormSection>

            <FormSection
              title={t`Address`}
              description={t`Location street and geography fields.`}
            >
              <AddressAutocompleteFields<LocationFormValues>
                idPrefix={idPrefix}
                control={control}
                register={register}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                inputClassName={inputClass}
                selectTriggerClassName={selectTriggerClass}
                labels={{ postalCode: t`ZIP Code` }}
                placeholders={{ address: t`Enter or search address...` }}
              />
            </FormSection>

            <FormSection
              title={t`Contact`}
              description={t`Optional location communication details.`}
              noBorder
            >
              <div className="flex gap-3">
                <FormField label={t`Email`} error={errors.email?.message}>
                  <Input
                    type="email"
                    placeholder={t`location@example.com`}
                    {...register("email")}
                    className={`${inputClass} ${errors.email ? "border-(--color-danger)" : ""}`}
                  />
                </FormField>
                <FormField label={t`Phone`} error={errors.phone?.message}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder={t`(555) 555-5555`}
                        // Digits are what is stored and sent; the mask is only
                        // what the field shows while they are typed.
                        value={formatUsPhone(field.value)}
                        onChange={(event) => field.onChange(normalizeUsPhone(event.target.value))}
                        inputMode="tel"
                        autoComplete="tel-national"
                        className={`${inputClass} ${errors.phone ? "border-(--color-danger)" : ""}`}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
          </form>

          <div className="p-5 border-t border-(--color-border) flex flex-col gap-3 bg-(--color-surface-content) shrink-0">
            {submitError && (
              <div className="flex items-center gap-2 rounded-lg bg-(--color-danger)/10 border border-(--color-danger)/20 px-4 py-2.5 text-sm text-(--color-danger-text)">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-sm text-(--color-text-secondary)">
                {t`Status defaults to Active • Organization ID auto-generated`}
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) border-(--color-border) text-base font-medium text-(--color-text-primary)"
                >
                  {t`Cancel`}
                </Button>
                <Button
                  form={`${idPrefix}-form`}
                  type="submit"
                  disabled={isPending || (isInternalOrgSelectorRequired && !selectedOrganizationId)}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) bg-(--color-action) text-(--color-action-fg) text-base font-medium hover:bg-(--color-action)/90 disabled:opacity-50"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t`Create Location`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CreateLocationDialogProps {
  defaultCompanyId?: number;
  defaultRegionId?: number;
  onCreated?: () => void;
  trigger?: ReactElement<{ onClick?: () => void; disabled?: boolean }>;
}

export function CreateLocationDialog({
  defaultCompanyId,
  defaultRegionId,
  onCreated,
  trigger,
}: CreateLocationDialogProps = {}) {
  const { t } = useLingui();
  const { user, hasPermission, hasRole } = useAuth();
  const [open, setOpen] = useState(false);

  const canCreateLocation = canAccessAction(
    { user, hasPermission, hasRole },
    {
      permissionsAny: ["location:create"],
    },
  );

  return (
    <>
      {trigger && isValidElement(trigger)
        ? cloneElement(trigger, {
            onClick: () => setOpen(true),
            disabled: !canCreateLocation || trigger.props.disabled,
          })
        : (
          <Button className="gap-2" onClick={() => setOpen(true)} disabled={!canCreateLocation}>
            <PlusCircle className="h-4 w-4" />
            {t`Create Location`}
          </Button>
        )}

      <LocationFormDialog
        open={open}
        onOpenChange={setOpen}
        defaultCompanyId={defaultCompanyId}
        defaultRegionId={defaultRegionId}
        onCreated={onCreated}
      />
    </>
  );
}